import { Injectable } from '@angular/core';
import { isNumber } from 'angular';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Comment } from '../../rl-common/comment/comment';
import { Visibility } from '../../rl-common/shared/shared/types/visibility.enum';
import { Annotation } from '../shared/annotation';
import { AnnotationConfig } from '../shared/annotation-config';
import { AnnotatorContainerDirective } from '../shared/annotator-container.directive';
import { AnnotationService } from './annotation.service';
import { AnnotatorJSService } from './annotator-js.service';
import { SearchParams } from './types';

@Injectable({
  providedIn: 'root'
})
export class AnnotationsStorageService {
  
  params : SearchParams = new SearchParams();
  
  comments$ = new Subject<any>();
  commentsArr : Comment[] = [];
  annotations : {[id : number] : Annotation[]} = {};
  annotationsMapByEntity : {[id : string] : {[id : string] : true}} = {};
  newAnnotation : Annotation;
  pagesAnns : {[pageNum : number] : any} = {};
  container : AnnotatorContainerDirective;
  waitingPages = [];
  loadedPages : {[pageNum : number] : boolean} = {};
  loaded : boolean = false;
  isInitilized : boolean = false;
  waitForInit : SearchParams[] = [];
  isReload : boolean;
  visibility$: BehaviorSubject<any> = new BehaviorSubject<any>(Visibility.PRIVATE);

  constructor(private annotationService : AnnotationService, private plugin : AnnotatorJSService) { }

  init(params : SearchParams){
    this.params = params;
    this.isInitilized = true;
    this.load(this.params);
  }

  load(params : SearchParams){
    if(!this.isInitilized){
      this.waitForInit.push(params);
      return;
    }
    this.annotationService.load(params).subscribe((annotations : Annotation[])=>{
      let firstLoad = !this.commentsArr?.length;
      if(firstLoad)this.comments$.next([]);
      annotations.forEach(a => {
        if(a.text) this.commentsArr.push(Object.assign(new Comment(a), {isNew : false}));
        this.addToStorage(a);
      });
      if (this.commentsArr.length > 0) this.comments$.next(this.commentsArr);
      //every time we load annotations we dont have all the pages in the view loaded 
      //so on scroll we load the page and then drow the annotaions on them
      //afterLoad is read after every page was loaded
      this.loaded = true;
      let loadPagesArr =  !this.waitingPages.length || !firstLoad || this.isReload ? Object.keys(this.loadedPages) : this.waitingPages;
      loadPagesArr.forEach(p => this.drawAnnotations(p, undefined, this.visibility$.value));

      this.waitingPages = [];
      this.loadWaitingParams();
      this.isReload = false;
    });
  }

  reDrawAll(visibility?: Visibility) {
    this.plugin.deleteAll();
    let loadPagesArr = Object.keys(this.loadedPages);
    loadPagesArr.forEach(p => this.drawAnnotations(+p, undefined, visibility));
  }

  reload() {
    this.commentsArr = [];
    this.isReload = true;
    this.pagesAnns = {};
    this.plugin.reset(() => this.init(this.params));
  }

  loadWaitingParams() {    
    while(this.waitForInit.length){
      let a = this.waitForInit.pop();
      if(!this.getEntityTypeMap(a)) this.load(a);
    }
  }
 
  drawAnnotations(page : number, rotation? : number, visibility? : Visibility) {
    if(!this.loaded) {
      this.waitingPages.push(page);
      return;
    }

    let annsToDraw = [];
    if(this.pagesAnns[page]){
      this.pagesAnns[page].forEach(a => {
        annsToDraw.push(this.annotations[a]);
      });      
    }  

    if(this.newAnnotation && this.newAnnotation.page == page){
      if(typeof this.newAnnotation.attributes !== 'string'){
        Object.assign(this.newAnnotation, {id : 'new', attributes : JSON.stringify(this.newAnnotation.attributes)})
      }
      annsToDraw.push(this.newAnnotation); 
    }

    if (visibility) {
      annsToDraw = annsToDraw.filter((e :Annotation) => e.visibility == visibility);
    }

    this.plugin.load(annsToDraw, rotation);
    this.loadedPages[page] = true;
  }

  addToStorage(a: Annotation) {
    if(!this.pagesAnns[a.page]) this.pagesAnns[a.page] = [];
    this.pagesAnns[a.page].push(a.id);
    this.annotations[a.id] = a;
    if(!this.getEntityTypeMap(a)) this.createEntityTypeMap(a);
    this.addToEntityTypeMap(a, a.id);
  }

  addCommentToArr(a : Annotation, newComment : boolean = false){
    this.commentsArr.push(Object.assign(new Comment(a), {isNew : newComment}));
    this.comments$.next(this.commentsArr);
  }

  updateCommentArr(i : number, a : Annotation){
    this.commentsArr[i] = new Comment(a);
    this.comments$.next(this.commentsArr);
  }

  removeCommentFromArr(i : number){
    if(i < 0 || i > this.commentsArr?.length) return;
    this.commentsArr.splice(i, 1);
    this.comments$.next(this.commentsArr);
  }

  deleteNewComment(){
    this.plugin.delete('new');
    this.removeCommentFromArr(this.commentsArr.findIndex(c => c.isNew));
  }

  deleteComment(comment : Comment) {
    this.delete(comment?.attachedId);
  }

  delete = (annId : string) => this.annotationService.delete(annId).subscribe(() => this.removeAnnotationFromViewById(annId));

  cancelEditComment = (comment : Comment) => !comment.isNew ? this.plugin.save(this.annotations[comment.attachedId]) : this.deleteNewComment();

  getCommentAnnotation = (commentIndex) : Annotation => this.annotations[this.commentsArr[commentIndex].attachedId];

  saveComment = (comment : Comment) => !comment.isNew ? this._updateComment(comment) : this._saveNewComment(comment.title, comment.visibility);
  _saveNewComment = (comment : string, visibility : Visibility) => this.newAnnotation.id ? this.__addNewCommentToAnnotation(comment, visibility) : this._createNewCommentAnnotation(comment, visibility);
  _createNewCommentAnnotation(comment : string, visibility : Visibility){
    let ann = this.addFields(this.newAnnotation);
    ann.text = comment;
    ann.visibility = visibility;
    this.saveNewAnnotation(this.newAnnotation, newAnn => this._updateCommentsWithNewComment(newAnn));
  }

  __addNewCommentToAnnotation(comment : string, visibility : Visibility){
    this.newAnnotation.attributes = JSON.parse(this.newAnnotation.attributes);
    this.setAnnOffset(this.newAnnotation);
    this.newAnnotation.attributes = JSON.stringify(this.newAnnotation.attributes);
    this.newAnnotation.text = comment;
    this.newAnnotation.visibility = visibility;
    
    this.annotationService.update(this.newAnnotation).subscribe(a => {
      this.plugin.save(a);
      this._updateCommentsWithNewComment(a);
    });
  }
  private _updateCommentsWithNewComment(a : Annotation) {
    this.updateCommentArr(this.commentsArr.findIndex(c => c.isNew), a);
  }

  _updateComment(comment : Comment) {
    this.annotationService.updateComment(comment.attachedId, comment.title, comment.visibility).subscribe(a => {
      Object.assign(comment, new Comment(a));
      this.plugin.save(a);
      this.updateCommentArr(this.commentsArr.findIndex(c => c.attachedId === a.id), a);
      this.annotations[a.id].visibility = a.visibility;
      this.reDrawAll(this.visibility$.value);
    });
  }

  editComment = (i: number) => this.plugin.edit(this.getCommentAnnotation(i));

  addFields(ann : Annotation){
    for(let key in this.params){
      if(!ann[key]) ann[key] = this.params[key];
    }

    if(!ann.attributes || typeof ann.attributes === 'string'){
      ann.attributes = {};
    }

    this.setAnnOffset(ann);

    if(ann.image_selection){
      ann.attributes.image_selection = JSON.stringify(ann.image_selection);
    }else{
      ann.attributes.ranges = JSON.stringify(ann.ranges);
      ann.attributes.quote = ann.quote;
    }
    
    return ann;
  }

  setAnnOffset(ann){ 
    ann.attributes.offsetTop = ann.offsetTop;
    ann.attributes.offsetLeft = ann.offsetLeft;
  }

  deleteAllComments(): void {
    this.annotationService.deleteAllComments(this.params).subscribe(() => {
      this.plugin.deleteAllComments();
      this.commentsArr = [];
      this.comments$.next([]);
    });
  }
  
  hoverAnnotation = (annId : string) => this.plugin.hover(annId);
  unhoverAnnotation = (annId : string) => this.plugin.unhover(annId);
  
  scrollToAnnotation(annId : string){
    let annPage : number = this.annotations[annId]?.page;
    if(!this.loadedPages[annPage]){
      this.drawAnnotations(annPage);
    }
    this.plugin.scroll(annId);
  }
  private _createNewAnnotation = (ann : Annotation) => this.newAnnotation = this.addFields(ann);

  saveNewAnnotation(ann? : Annotation, onSave? : (a) => void){
    if(ann !== this.newAnnotation) this._createNewAnnotation(ann);
    ann.attributes = JSON.stringify(ann.attributes);
    delete ann.id;
    this.annotationService.save(this.newAnnotation).subscribe(a => {
      this.plugin.save(a);
      delete this.newAnnotation;
      this.addToStorage(a);
      if(onSave) onSave(a);
    });
  }

  editNewAnnotation(ann : Annotation){
    this._createNewAnnotation(ann);
    this.plugin.edit(this.newAnnotation, () => {
      this.setAnnOffset(ann);
      this.addCommentToArr(ann, true);
    });
  }

  addCommentToAnnotation(ann : Annotation){
    this.newAnnotation = ann;
    this.plugin.edit(ann,  () => this.addCommentToArr(ann, true));
  }

  removeAnnotations(config: AnnotationConfig[]) {
    config.forEach(ac => {
      let anns = this.annotationsMapByEntity[ac.entityId + ac.entityType];
      this.removeAllCommentsByAnnsIds(anns);
      Object.keys(anns).forEach(a => this.removeAnnotationFromViewById(a));
      delete this.annotationsMapByEntity[ac.entityId + ac.entityType];
    });
  }

  removeAllCommentsByAnnsIds(annsIds : {[id :string] : true}){
    this.commentsArr = this.commentsArr.filter(c => !annsIds[c.attachedId]);
    this.comments$.next(this.commentsArr);
  }

  removeCommentFromView(commentIndex : number){
    if(this.commentsArr?.length < commentIndex) return;
    this.commentsArr.splice(commentIndex, 1);
    this.comments$.next(this.commentsArr);
  }

  removeAnnotationFromViewById(annId : string){
    let ann = this.annotations[annId];
    if(ann) this.removeAnnotationFromView(ann);
  }

  removeAnnotationFromView(ann : Annotation){
    if(ann.parentId) return;
    this.plugin.delete(ann);
    this.pagesAnns[ann.page].splice(this.pagesAnns[ann.page].indexOf(ann.id), 1);
    delete this.annotations[ann.id];
    this.commentsArr.splice(this.commentsArr.findIndex(c => c.attachedId === ann.id), 1);
  }  
  
  addToEntityTypeMap = (a: Annotation, id: string) => this.annotationsMapByEntity[a.entityId + a.entityType][id] = true;
  createEntityTypeMap = (a: Annotation) => this.annotationsMapByEntity[a.entityId + a.entityType] = {};
  getEntityTypeMap = (a: {entityType : string, entityId : string }) => this.annotationsMapByEntity[a.entityId + a.entityType];

  reply = (annIdToReply : string, replyText : string) => {
    return this.annotationService.reply(annIdToReply, replyText);
  }

  getVisibility(): Observable<any> {
    return this.visibility$;
  }

  setVisibility(visibility: Visibility): void {
    this.visibility$.next(visibility);
  }

  getFilteredComments() {
    return this.commentsArr.filter((e: Comment) => e.visibility === this.visibility$.value);
  }

  getFilteredCommentsLength() {
    return this.commentsArr.reduce((total, comment) => (comment.visibility === this.visibility$.value ? total + 1 + (comment.visibility === Visibility.PUBLIC ? comment.subComments.length : 0) : total), 0);
  }
  
  getCommentById(id: string) {
    return this.commentsArr.find((c) => c.attachedId === id);
  }
}